import LangMenuFlag from '@/components/lang/lang-menu-flag';
import UpdateBanner from '@/components/update-banner/update-banner';
import { CAPABILITIES, IS_NATIVE } from '@/config/platform';
import { COLORS } from '@/config';
import { cn } from '@/lib/utils';
import { REFRESH_TOKEN } from '@/utils/utils';
import { ButtonBase } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  createRootRoute,
  Outlet,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import 'dayjs/locale/fr';
import { useEffect } from 'react';
import { FaMinus, FaRegSquare } from 'react-icons/fa';
import { FaXmark } from 'react-icons/fa6';
import { z } from 'zod';

const searchSchema = z.object({
  tab: z.string().optional(),
});

export const Route = createRootRoute({
  component: () => {
    const routerState = useRouterState();
    const router = useRouter();
    useEffect(() => {
      const isLoggedIn = window.store.get(REFRESH_TOKEN);
      if (isLoggedIn && routerState.location.pathname.startsWith('/auth')) {
        router.navigate({ to: '/dashboard/accounts' });
      } else if (
        !isLoggedIn &&
        routerState.location.pathname.startsWith('/dashboard')
      ) {
        router.navigate({ to: '/auth' });
      }
      window.electron?.ipcRenderer.on('custom-url', (_, url: string) => {
        if (!url.startsWith('http')) {
          const newUrl = new URL(url);
          const params = Object.fromEntries(newUrl.searchParams);
          router.navigate({
            to: newUrl.pathname,
            search: params,
          });
        }
      });
      // Pendant natif : le lien de l'e-mail de réinitialisation du mot de
      // passe (kara-trading://auth/reset-password?token=…) rouvre l'app. Seul
      // ce lien est suivi, avec son seul jeton : n'importe quelle page ou
      // n'importe quel paramètre ouvrirait la porte à un lien piégé (jetons de
      // session injectés dans /auth, pages internes).
      if (IS_NATIVE) {
        void import('@capacitor/app').then(({ App }) =>
          App.addListener('appUrlOpen', ({ url }) => {
            if (url.startsWith('http')) return;
            // WebKit range le premier segment après `kara-trading://` dans
            // l'hôte (`auth`) et la suite dans le chemin (`/reset-password`).
            const { host, pathname, searchParams } = new URL(url);
            const token = searchParams.get('token');
            if (`/${host}${pathname}` !== '/auth/reset-password' || !token) {
              return;
            }
            router.navigate({ to: '/auth/reset-password', search: { token } });
          }),
        );
      }
    }, []);

    return (
      <div className={cn(CAPABILITIES.windowChrome && 'grid grid-rows-[32px,1fr]')}>
        {CAPABILITIES.windowChrome && (
          <div className="flex justify-between items-center">
            <div className="title-bar flex justify-end items-center px-1 gap-2">
              <LangMenuFlag className="title-bar-button ml-5" />
              <ButtonBase
                className="title-bar-button shrink-0 h-fit"
                sx={{
                  p: 0.5,
                  borderRadius: '4px',
                  background: COLORS.grey[800],
                  color: COLORS.grey[500],
                }}
                onClick={() => window.api.minimize()}
              >
                <FaMinus size={12} />
              </ButtonBase>
              <ButtonBase
                className="title-bar-button shrink-0 h-fit"
                sx={{
                  p: 0.5,
                  borderRadius: '4px',
                  background: COLORS.grey[800],
                  color: COLORS.grey[500],
                }}
                onClick={() => window.api.maximize()}
              >
                <FaRegSquare size={12} />
              </ButtonBase>
              <ButtonBase
                className="title-bar-button shrink-0 h-fit"
                sx={{
                  p: 0.25,
                  borderRadius: '4px',
                  background: COLORS.grey[800],
                  color: COLORS.grey[500],
                }}
                onClick={() => window.api.close()}
              >
                <FaXmark size={16} />
              </ButtonBase>
            </div>
          </div>
        )}
        <div>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Outlet />
          </LocalizationProvider>
        </div>
        <UpdateBanner />
      </div>
    );
  },
  validateSearch: (search) => searchSchema.parse(search),
});
